import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Home, Eye, EyeOff, ArrowLeft, Check, X } from "lucide-react";
import { Link, useLocation } from "wouter";
import Header from "@/components/Header";
import GoogleSign from "./GoogleSign";
import axios from "axios";
import API_URL from "@/config";
import { useDispatch, useSelector } from "react-redux";
import { login } from "@/Redux/authSlice";
import useDocumentTitle from "@/components/Helmet";

// Form schemas
const loginSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
    password: z.string().min(1, "Password is required"),
    rememberMe: z.boolean().default(false),
});


const forgotPasswordSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
});

type LoginForm = z.infer<typeof loginSchema>;
type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

type AuthMode = "login" | "register" | "forgot";

export default function Login() {
    useDocumentTitle("Login");
    const {isAuthenticated} = useSelector((state: any) => state.auth);
    const dispatch = useDispatch();
    const [location, navigate] = useLocation();
    const [authMode, setAuthMode] = useState<AuthMode>("login");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
        if(isAuthenticated) navigate("/");
    },[isAuthenticated])

    // Forms
    const loginForm = useForm<LoginForm>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: "",
            password: "",
            rememberMe: false,
        },
    });



    const forgotPasswordForm = useForm<ForgotPasswordForm>({
        resolver: zodResolver(forgotPasswordSchema),
        defaultValues: {
            email: "",
        },
    });

    // Password validation for register form
    const [passwordValidation, setPasswordValidation] = useState({
        length: false,
        uppercase: false,
        number: false,
    });

    const validatePasswordRequirements = (password: string) => {
        setPasswordValidation({
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            number: /[0-9]/.test(password),
        });
    };

    // Watch password changes for validation


    // Handle form submissions
    const handleLogin = async (data: LoginForm) => {
        setIsLoading(true);
        try {

            await axios.post(API_URL + "/api/auth/login", data)
                .then((res) => {
                    if (res?.data?.success) {
                        dispatch(login({ user: res?.data.user, token: res?.data.token }));
                        toast({
                            variant: "success",
                            title: "Login successful!",
                            description: "Redirecting to dashboard...",
                        });
                        sessionStorage.setItem("user", JSON.stringify(res?.data?.user));
                        sessionStorage.setItem("token", res?.data?.token);
                        navigate("/");
                    }
                    else {
                        toast({
                            variant: "destructive",
                            title: "Login failed!",
                            description: res?.data?.message,
                        });
                    }
                })
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Login failed",
                description: "Please check your credentials and try again.",
            });
        } finally {
            setIsLoading(false);
        }
    };



    const handleForgotPassword = async (data: ForgotPasswordForm) => {
        setIsLoading(true);
        try {
            const res = await axios.post(API_URL + "/api/auth/forgot-password", data);

            if (res.data.success) {
                toast({
                    variant: "success",
                    title: "Reset link sent!",
                    description: "Check your email for password reset instructions.",
                });
                setTimeout(() => setAuthMode("login"), 2000);
            } else {
                toast({
                    variant: "destructive",
                    title: "Error",
                    description: res.data.message,
                });
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error",
                description: error?.response?.data?.message || "Failed to send reset link",
            });
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            {/* Auth Form */}
            <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{
                backgroundImage: "url('/background.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                minHeight: "92vh"
            }}>
                <div className="max-w-xl w-full space-y-8">
                    <Card className="bg-white rounded-2xl shadow-xl auth-transition">
                        {/* Login Form */}
                        {authMode === "login" && (
                            <form onSubmit={loginForm.handleSubmit(handleLogin)}>
                                <CardHeader className="text-center pb-6">
                                    <CardTitle className="text-3xl font-bold text-gray-900">Welcome Back</CardTitle>
                                    <CardDescription className="text-gray-600">
                                        Sign in to your PropertyHub account
                                    </CardDescription>
                                </CardHeader>

                                <CardContent className="space-y-3">
                                    <GoogleSign />

                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <Separator className="w-full" />
                                        </div>
                                        <div className="relative flex justify-center text-sm">
                                            <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                                        </div>
                                    </div>

                                    {/* Email Input */}
                                    <div className="space-y-2">
                                        <Label htmlFor="login-email">Email address</Label>
                                        <Input
                                            id="login-email"
                                            type="email"
                                            placeholder="Enter your email"
                                            className="form-input"
                                            data-testid="input-email"
                                            {...loginForm.register("email")}
                                        />
                                        {loginForm.formState.errors.email && (
                                            <p className="text-sm text-red-600">{loginForm.formState.errors.email.message}</p>
                                        )}
                                    </div>

                                    {/* Password Input */}
                                    <div className="space-y-2">
                                        <Label htmlFor="login-password">Password</Label>
                                        <div className="relative">
                                            <Input
                                                id="login-password"
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Enter your password"
                                                className="form-input pr-12"
                                                data-testid="input-password"
                                                {...loginForm.register("password")}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                                onClick={() => setShowPassword(!showPassword)}
                                                data-testid="button-toggle-password"
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-gray-400" />
                                                )}
                                            </Button>
                                        </div>
                                        {loginForm.formState.errors.password && (
                                            <p className="text-sm text-red-600">{loginForm.formState.errors.password.message}</p>
                                        )}
                                    </div>

                                    {/* Remember Me & Forgot Password */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="remember-me"
                                                {...loginForm.register("rememberMe")}
                                            />
                                            <Label htmlFor="remember-me" className="text-sm">Remember me</Label>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="link"
                                            className="text-sm text-primary hover:text-primary-600"
                                            onClick={() => setAuthMode("forgot")}
                                            data-testid="button-forgot-password"
                                        >
                                            Forgot password?
                                        </Button>
                                    </div>

                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        className="w-full btn-hover"
                                        disabled={isLoading}
                                        data-testid="button-signin"
                                    >
                                        {isLoading ? "Signing in..." : "Sign In"}
                                    </Button>

                                    {/* Switch to Register */}
                                    <p className="text-center text-sm text-gray-600">
                                        Don't have an account?{" "}
                                        <Button
                                            type="button"
                                            variant="link"
                                            className="text-primary hover:text-primary-600 p-0"
                                            onClick={() => navigate("/register")}
                                            data-testid="button-switch-register"
                                        >
                                            Sign up
                                        </Button>
                                    </p>
                                </CardContent>
                            </form>
                        )}

                        {/* Register Form */}


                        {/* Forgot Password Form */}
                        {authMode === "forgot" && (
                            <form onSubmit={forgotPasswordForm.handleSubmit(handleForgotPassword)}>
                                <CardHeader className="text-center pb-6">
                                    <CardTitle className="text-3xl font-bold text-gray-900">Reset Password</CardTitle>
                                    <CardDescription className="text-gray-600">
                                        Enter your email to receive a password reset link
                                    </CardDescription>
                                </CardHeader>

                                <CardContent className="space-y-6">
                                    {/* Email Input */}
                                    <div className="space-y-2">
                                        <Label htmlFor="forgot-email">Email address</Label>
                                        <Input
                                            id="forgot-email"
                                            type="email"
                                            placeholder="Enter your email"
                                            className="form-input"
                                            data-testid="input-forgot-email"
                                            {...forgotPasswordForm.register("email")}
                                        />
                                        {forgotPasswordForm.formState.errors.email && (
                                            <p className="text-sm text-red-600">{forgotPasswordForm.formState.errors.email.message}</p>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <Button
                                        type="submit"
                                        className="w-full btn-hover"
                                        disabled={isLoading}
                                        data-testid="button-send-reset"
                                    >
                                        {isLoading ? "Sending..." : "Send Reset Link"}
                                    </Button>

                                    {/* Back to Login */}
                                    <div className="text-center">
                                        <Button
                                            type="button"
                                            variant="link"
                                            className="text-primary hover:text-primary-600"
                                            onClick={() => setAuthMode("login")}
                                            data-testid="button-back-login"
                                        >
                                            <ArrowLeft className="w-4 h-4 mr-2" />
                                            Back to Sign In
                                        </Button>
                                    </div>
                                </CardContent>
                            </form>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
}
